import { PlayerActionType } from '@core/actions';
import { MeldType, type TMeld } from '@core/meld';
import { findHuPatterns } from '@core/hu';
import { scoreHu } from '@core/scoring';
import { AIStrength, AIStyle, type IAISeatProfile, type TAIStrength } from '@core/table-ai';
import { TILE_INDEX_TO_SUIT_RANK, tileToIndex, type TTile } from '@core/tile';
import { createSeedRng } from '@core/wall';
import { CHandAnalysis } from './hand-analysis';
import { type IAIObservation, type IAICandidate, unknownKindCounts } from './observation';
import { createRuleStrategyAI } from './strategy-rule';

export interface IAIDecisionResult {
  readonly index: number;
  readonly evaluated: number;
  readonly elapsedMs: number;
  readonly truncated: boolean;
}
const canonicalTile = (kind: number, id: number): TTile => ({
  ...TILE_INDEX_TO_SUIT_RANK[kind]!,
  id,
});

/** 固定基準策略同時用於代打與 Worker 備援；輸入仍只有白名單觀察。 */
export const baselineDecision = (o: IAIObservation, seed: number): number => {
  const hu = o.candidates.findIndex((c) => c.type === PlayerActionType.Hu);
  if (hu >= 0) return hu;
  if (o.candidates.length === 1) return 0;
  if (o.phase === 'initial_kong') return 0;
  let id = 0;
  const hand = {
    concealed: o.concealed.map((k) => canonicalTile(k, id++)),
    melds: o.melds[o.seat]!.map((m) => ({
      type: m.type,
      tiles: m.kinds.map((k) => canonicalTile(k, id++)),
    })),
    flowers: o.flowers[o.seat]!.map((k) => canonicalTile(k, id++)),
  };
  const ctx = {
    mySeat: o.seat,
    declaredTenpai: o.declared[o.seat]!,
    wallRemaining: o.wallRemaining,
    discards: o.discards.map((river) => river.map((k) => canonicalTile(k, id++))),
  };
  const ai = createRuleStrategyAI(createSeedRng(seed));
  const drawnKind = o.drawn;
  const action =
    o.phase === 'turn'
      ? ai.decideTurn(
          hand,
          drawnKind === null
            ? null
            : hand.concealed.find(
                (t) =>
                  t.suit === TILE_INDEX_TO_SUIT_RANK[drawnKind!]!.suit &&
                  t.rank === TILE_INDEX_TO_SUIT_RANK[drawnKind!]!.rank
              )!,
          ctx
        )
      : o.incoming
        ? ai.decideReaction(hand, canonicalTile(o.incoming.kind, id++), o.incoming.seat, ctx)
        : { type: PlayerActionType.Pass };
  const found = o.candidates.findIndex(
    (c) =>
      c.type === action.type &&
      (action.type !== PlayerActionType.Discard || c.kind === o.concealed[action.tile.id]) &&
      (!('option' in action) ||
        !('handTiles' in action.option) ||
        JSON.stringify(c.removed) ===
          JSON.stringify(
            action.option.handTiles.map((t) => o.concealed[t.id]).sort((a, b) => a! - b!)
          ))
  );
  return found >= 0
    ? found
    : Math.max(
        0,
        o.candidates.findIndex((c) => c.type === PlayerActionType.Pass)
      );
};

const after = (o: IAIObservation, c: IAICandidate): number[] => {
  const kinds = [...o.concealed];
  for (const kind of c.removed) {
    const i = kinds.indexOf(kind);
    if (i >= 0) kinds.splice(i, 1);
  }
  return kinds;
};

/** 風險是估計而非安全保證；麻將沒有將他家曾打牌視為絕對安全的規則。 */
const riskOf = (o: IAIObservation, kind: number): number =>
  o.declared.reduce((sum, ready, seat) => {
    if (seat === o.seat) return sum;
    const river = o.discards[seat]!;
    const pressure = ready ? 1 : Math.min(0.45, river.length / 40 + o.melds[seat]!.length * 0.04);
    const seenBefore = river.includes(kind) ? 0.45 : 1;
    const central = kind < 27 && kind % 9 >= 2 && kind % 9 <= 6 ? 1.2 : 0.8;
    return sum + pressure * seenBefore * central;
  }, 0);

// ---- 強度參數：三桌強度只在這裡調整；調整後用 `pnpm eval:strength` 重新驗收 ----
export interface IStrengthTuning {
  readonly shantenWeight: number; // 每一級向聽數的分數（要明顯大於有效張數加分，否則會為了牌面寬而退向聽）
  readonly ukeireWeight: number; // 有效張數權重（新手不太算牌效）
  readonly defenceScale: number; // 防守權重倍率（新手幾乎不防守）
  readonly readsThreat: boolean; // 用牌河／副露／巡目推估他家聽牌與各牌危險度
  readonly foldShanten: number; // 有人威脅高且自己向聽數 ≥ 此值時棄胡打安全牌
  readonly callBonus: number; // 吃碰加分（新手看到能吃碰就想吃碰）
  readonly honorKeep: number; // 每張孤張字牌的保留加分（新手捨不得打字牌）
  readonly mistakeRate: number; // 改選次佳的機率
  readonly mistakeWindow: number; // 次佳可接受的分數差距
  readonly mistakeAnyShanten: boolean; // 次佳候選是否不限同向聽
  readonly foldThreat: number; // 有任一家聽牌機率 ≥ 此值才考慮棄胡
  readonly foldRiskWeight: number; // 棄胡時危險度的權重（壓過牌效）
  readonly readDefenceWeight: number; // 會讀牌時，一般進攻的危險度權重
  readonly tenpaiValueCap: number; // 聽牌時台數價值加分上限（高強度）
  readonly tenpaiValueScale: number; // 聽牌時台數價值加分倍率（高強度）
  readonly lookaheadCandidates: number; // 推演前幾名候選（高強度）
  readonly lookaheadDraws: number; // 每個候選抽樣幾種有效摸牌（高強度；≥ 34 即全部精確計算）
  readonly lookaheadWeight: number; // 下一層有效張數的權重（高強度）
  readonly menqingCallCost: number; // 門清時吃碰的額外代價（放棄門清台數）
  readonly menqingCallLateWall: number; // 牌牆剩餘 ≤ 此值時不再計門清代價（後期速度優先）
  readonly shapeShanten: number; // 向聽數 1～此值時加做牌方向分（0＝不做）
  readonly flushWeight: number; // 混一色／清一色方向分
  readonly flushMaxOff: number; // 雜色張數 ≥ 此值就不算一色方向
  readonly toitoiWeight: number; // 碰碰胡方向分
  readonly toitoiMinUnits: number; // 對子＋刻子數超過此值才開始給碰碰胡方向分
}

/**
 * 參照玩家：策略版本 1 的中強度原行為（平衡打法），固定不調。
 * 強度評測與經濟模擬用它代表「會打牌的一般玩家」，各桌強度都以它為基準量測。
 */
export const REFERENCE_TUNING: IStrengthTuning = {
  shantenWeight: 100,
  ukeireWeight: 1.5,
  defenceScale: 1,
  readsThreat: false,
  foldShanten: Infinity,
  callBonus: 0,
  honorKeep: 0,
  mistakeRate: 0,
  mistakeWindow: 0,
  mistakeAnyShanten: false,
  foldThreat: 0.5,
  foldRiskWeight: 400,
  readDefenceWeight: 30,
  tenpaiValueCap: 25,
  tenpaiValueScale: 3,
  lookaheadCandidates: 3,
  lookaheadDraws: 4,
  lookaheadWeight: 0.1,
  menqingCallCost: 0,
  menqingCallLateWall: 0,
  shapeShanten: 0,
  flushWeight: 0,
  flushMaxOff: 4,
  toitoiWeight: 0,
  toitoiMinUnits: 3,
};

const STRENGTH_TUNING: Record<TAIStrength, IStrengthTuning> = {
  [AIStrength.Low]: {
    shantenWeight: 100,
    ukeireWeight: 1.5,
    defenceScale: 0.6,
    readsThreat: false,
    foldShanten: Infinity,
    callBonus: 3,
    honorKeep: 2,
    mistakeRate: 0.25,
    mistakeWindow: 15,
    mistakeAnyShanten: false,
    foldThreat: 0.5,
    foldRiskWeight: 400,
    readDefenceWeight: 30,
    tenpaiValueCap: 25,
    tenpaiValueScale: 3,
    lookaheadCandidates: 3,
    lookaheadDraws: 4,
    lookaheadWeight: 0.1,
    menqingCallCost: 0,
    menqingCallLateWall: 0,
    shapeShanten: 0,
    flushWeight: 0,
    flushMaxOff: 4,
    toitoiWeight: 0,
    toitoiMinUnits: 3,
  },
  // 進階桌：參照玩家的牌效，但防守較鬆、看到能吃碰就比較想吃碰（2026-10-26 調校）
  [AIStrength.Medium]: { ...REFERENCE_TUNING, defenceScale: 0.6, callBonus: 2 },
  [AIStrength.High]: {
    shantenWeight: 100,
    ukeireWeight: 1.5,
    defenceScale: 1,
    readsThreat: true,
    foldShanten: Infinity,
    callBonus: 0,
    honorKeep: 0,
    mistakeRate: 0,
    mistakeWindow: 0,
    mistakeAnyShanten: false,
    foldThreat: 0.5,
    foldRiskWeight: 400,
    readDefenceWeight: 8,
    tenpaiValueCap: 60,
    tenpaiValueScale: 6,
    lookaheadCandidates: 3,
    lookaheadDraws: 4,
    lookaheadWeight: 0.1,
    menqingCallCost: 40,
    menqingCallLateWall: 20,
    shapeShanten: 0,
    flushWeight: 0,
    flushMaxOff: 4,
    toitoiWeight: 0,
    toitoiMinUnits: 3,
  },
};

const HONOR_KIND_START = 27;
const TOITOI_UNITS = 6; // 碰碰胡：五組刻子＋一對眼

// ---- 高強度的聽牌推估與危險度（只用公開資訊；台灣麻將沒有振聽，現物也只是降低危險） ----
const THREAT_BY_MELDS = [0, 0.12, 0.32, 0.6, 0.85, 0.95]; // 副露組數 → 聽牌機率
const THREAT_TURN_START = 5; // 捨牌數超過這個值後，巡目開始提高聽牌機率
const THREAT_PER_TURN = 0.05;
const THREAT_TURN_CAP = 0.7;
const DANGER_GENBUTSU = 0.35; // 該家打過的牌
const DANGER_HONOR_BY_VISIBLE = [1, 0.75, 0.3, 0.05]; // 字牌已見張數（不含該家暗牌）
const DANGER_TERMINAL = 0.6; // 么九
const DANGER_NEAR_TERMINAL = 0.85; // 二、八
const DANGER_SUJI_FULL = 0.5; // 兩邊筋牌都被該家打過（4、5、6）
const DANGER_SUJI_HALF = 0.75; // 一邊筋牌被打過
const DANGER_DEALER = 1.3; // 莊家聽牌的代價較高

const visibleKindCounts = (o: IAIObservation): number[] => {
  const counts = Array<number>(34).fill(0);
  for (const k of o.concealed) counts[k]!++;
  for (const river of o.discards) for (const k of river) counts[k]!++;
  for (const melds of o.melds) for (const meld of melds) for (const k of meld.kinds) counts[k]!++;
  return counts;
};

const threatOf = (o: IAIObservation, seat: number): number => {
  if (o.declared[seat]) return 1;
  const fromMelds = THREAT_BY_MELDS[Math.min(5, o.melds[seat]!.length)]!;
  const turns = o.discards[seat]!.length;
  const fromTurns = Math.min(
    THREAT_TURN_CAP,
    Math.max(0, turns - THREAT_TURN_START) * THREAT_PER_TURN
  );
  return 1 - (1 - fromMelds) * (1 - fromTurns);
};

const dangerOf = (
  o: IAIObservation,
  seat: number,
  kind: number,
  visible: readonly number[]
): number => {
  const river = o.discards[seat]!;
  let danger = river.includes(kind) ? DANGER_GENBUTSU : 1;
  if (kind >= 27) return danger * DANGER_HONOR_BY_VISIBLE[Math.min(3, visible[kind]!)]!;
  const rank = kind % 9;
  if (rank === 0 || rank === 8) danger *= DANGER_TERMINAL;
  else if (rank === 1 || rank === 7) danger *= DANGER_NEAR_TERMINAL;
  const low = rank >= 3 && river.includes(kind - 3);
  const high = rank <= 5 && river.includes(kind + 3);
  const sujiSides = (rank >= 3 ? 1 : 0) + (rank <= 5 ? 1 : 0);
  const sujiHits = (low ? 1 : 0) + (high ? 1 : 0);
  if (sujiHits > 0) danger *= sujiHits === sujiSides ? DANGER_SUJI_FULL : DANGER_SUJI_HALF;
  return danger;
};

/** 打出這張牌放槍的估計代價（各家聽牌機率 × 危險度）。 */
const readRiskOf = (o: IAIObservation, kind: number, visible: readonly number[]): number =>
  o.discards.reduce((sum, _, seat) => {
    if (seat === o.seat) return sum;
    const dealer = seat === o.dealerSeat ? DANGER_DEALER : 1;
    return sum + threatOf(o, seat) * dangerOf(o, seat, kind, visible) * dealer;
  }, 0);

const maxThreatOf = (o: IAIObservation): number =>
  Math.max(...o.discards.map((_, seat) => (seat === o.seat ? 0 : threatOf(o, seat))));

/**
 * 做牌方向分（高強度、一～數向聽時）：往混一色／清一色、碰碰胡靠攏的程度，0～1 再乘權重。
 * 只看自己的牌與副露，聽牌後改由實際台數評估（tenpaiValue）。
 */
const shapeBonusOf = (
  concealed: readonly number[],
  melds: readonly TMeld[],
  tuning: IStrengthTuning
): number => {
  const all = [...concealed, ...melds.flatMap((m) => m.tiles.map(tileToIndex))];
  let bonus = 0;
  if (tuning.flushWeight > 0) {
    let minOff = Infinity;
    for (let suit = 0; suit < 3; suit++)
      minOff = Math.min(
        minOff,
        all.filter((k) => k < HONOR_KIND_START && Math.floor(k / 9) !== suit).length
      );
    bonus += tuning.flushWeight * Math.max(0, 1 - minOff / tuning.flushMaxOff);
  }
  if (tuning.toitoiWeight > 0 && !melds.some((m) => m.type === MeldType.Chi)) {
    const counts = new Map<number, number>();
    for (const k of concealed) counts.set(k, (counts.get(k) ?? 0) + 1);
    const units = [...counts.values()].filter((n) => n >= 2).length + melds.length;
    bonus +=
      tuning.toitoiWeight *
      Math.max(0, (units - tuning.toitoiMinUnits) / (TOITOI_UNITS - tuning.toitoiMinUnits));
  }
  return bonus;
};

/** 候選執行後的自身副露（供台數計算）。 */
const meldsAfter = (o: IAIObservation, candidate: IAICandidate, isNewMeld: boolean): TMeld[] => {
  const melds: TMeld[] = o.melds[o.seat]!.map((m) => ({
    type: m.type,
    tiles: m.kinds.map((k, i) => canonicalTile(k, 100 + i)),
  }));
  if (isNewMeld) {
    const type = candidate.type as TMeld['type'];
    const meldKinds =
      candidate.type === PlayerActionType.KongConcealed
        ? candidate.removed
        : [...candidate.removed, o.incoming!.kind];
    melds.push({
      type,
      tiles: meldKinds.map((k, i) => canonicalTile(k, 200 + i)),
    });
  } else if (candidate.type === PlayerActionType.KongAdded) {
    const index = melds.findIndex(
      (m) =>
        m.type === MeldType.Pong &&
        m.tiles[0]!.suit === TILE_INDEX_TO_SUIT_RANK[candidate.kind!]!.suit &&
        m.tiles[0]!.rank === TILE_INDEX_TO_SUIT_RANK[candidate.kind!]!.rank
    );
    if (index >= 0)
      melds[index] = {
        type: MeldType.KongAdded,
        tiles: [...melds[index]!.tiles, canonicalTile(candidate.kind!, 250)],
      };
  }
  return melds;
};

/** 以專案正式台數規則計算：concealed 為胡牌前的暗牌，winKind 為胡張。 */
const taiOf = (
  o: IAIObservation,
  melds: readonly TMeld[],
  concealed: readonly number[],
  winKind: number,
  selfDraw: boolean
): number => {
  const hand = {
    concealed: concealed.map(canonicalTile),
    melds: [...melds],
    flowers: o.flowers[o.seat]!.map(canonicalTile),
  };
  const patterns = findHuPatterns(hand, canonicalTile(winKind, 300), selfDraw);
  return Math.max(
    0,
    ...patterns.map((pattern) =>
      scoreHu({
        pattern,
        hand,
        isDealer: o.seat === o.dealerSeat,
        dealerStreak: o.dealerStreak,
        seatWind: o.seatWind,
        prevailingWind: o.prevailingWind,
        wallRemaining: o.wallRemaining,
        isAfterKong: false,
        isRobbingKong: false,
        declaredTenpai: true,
      }).reduce((sum, item) => sum + item.points, 0)
    )
  );
};

export const decideObservation = (
  o: IAIObservation,
  profile: IAISeatProfile,
  budgetMs = 4500,
  progress?: (result: IAIDecisionResult) => void,
  tuningOverride?: Partial<IStrengthTuning> // 僅供強度評測、調參與經濟模擬，正式遊戲不傳
): IAIDecisionResult => {
  const started = performance.now();
  const seed = decisionSeed(o, profile.seed);
  let best = baselineDecision(o, seed);
  let evaluated = 0;
  let truncated = false;
  const result = (): IAIDecisionResult => ({
    index: best,
    evaluated,
    elapsedMs: performance.now() - started,
    truncated,
  });
  if (o.candidates.some((c) => c.type === PlayerActionType.Hu) || o.candidates.length === 1)
    return result();
  const analysis = new CHandAnalysis();
  const unknown = unknownKindCounts(o);
  const fixed = o.melds[o.seat]!.length;
  const tuning = { ...STRENGTH_TUNING[profile.strength], ...tuningOverride };
  const visible = tuning.readsThreat ? visibleKindCounts(o) : [];
  // 棄胡：有人威脅高、自己離聽牌還遠時，改以安全為優先（只有會讀牌的強度才做）
  const folding =
    tuning.readsThreat &&
    maxThreatOf(o) >= tuning.foldThreat &&
    analysis.shanten(o.concealed, fixed) >= tuning.foldShanten;
  const scores: {
    index: number;
    score: number;
    shanten: number;
    kinds: number[];
    nextFixed: number;
    useful: number[];
  }[] = [];
  const expired = (): boolean => performance.now() - started >= budgetMs;
  const value = (kinds: readonly number[]): number => {
    const all = [...kinds, ...o.melds[o.seat]!.flatMap((m) => m.kinds)];
    const suits = new Set(all.filter((k) => k < 27).map((k) => Math.floor(k / 9)));
    const honors = [31, 32, 33];
    const winds = ['east', 'south', 'west', 'north'];
    honors.push(27 + winds.indexOf(o.seatWind), 27 + winds.indexOf(o.prevailingWind));
    const honorValue = honors.reduce(
      (sum, k) => sum + Math.min(3, all.filter((v) => v === k).length) / 3,
      0
    );
    return honorValue + (suits.size === 1 ? 2 : 0);
  };
  for (let index = 0; index < o.candidates.length; index++) {
    if (expired()) {
      truncated = true;
      break;
    }
    const candidate = o.candidates[index]!;
    let kinds = after(o, candidate);
    const isNewMeld =
      candidate.type === PlayerActionType.Chi ||
      candidate.type === PlayerActionType.Pong ||
      candidate.type === PlayerActionType.KongConcealed ||
      candidate.type === PlayerActionType.KongExposed;
    const nextFixed = fixed + (isNewMeld ? 1 : 0);
    // 吃碰後必須捨一張；不能只獎勵副露數而漏算拆牌代價。
    if (candidate.type === PlayerActionType.Chi || candidate.type === PlayerActionType.Pong) {
      let bestAfter = kinds;
      let distance = Infinity;
      for (let i = 0; i < kinds.length; i++) {
        const remaining = kinds.filter((_, n) => i !== n);
        const shanten = analysis.shanten(remaining, nextFixed);
        if (shanten < distance) {
          distance = shanten;
          bestAfter = remaining;
        }
      }
      kinds = bestAfter;
    }
    const shanten = analysis.shanten(kinds, nextFixed);
    let ukeire = 0;
    const useful: number[] = [];
    for (let k = 0; k < 34; k++) {
      if (expired()) {
        truncated = true;
        break;
      }
      if (unknown[k]! > 0 && analysis.shanten([...kinds, k], nextFixed) < shanten) {
        ukeire += unknown[k]!;
        useful.push(k);
      }
    }
    if (truncated) break; // 未完成評估不可與完整候選比較。
    const defence =
      profile.style === AIStyle.Cautious ? 26 : profile.style === AIStyle.Attack ? 8 : 16;
    const isDiscard = candidate.type === PlayerActionType.Discard;
    const isCall =
      candidate.type === PlayerActionType.Chi || candidate.type === PlayerActionType.Pong;
    const readRisk = tuning.readsThreat && isDiscard ? readRiskOf(o, candidate.kind!, visible) : 0;
    const defencePenalty = tuning.readsThreat
      ? readRisk * tuning.readDefenceWeight * (defence / 16)
      : (isDiscard ? riskOf(o, candidate.kind!) : 0) * defence * tuning.defenceScale;
    const isMenqing = o.melds[o.seat]!.every((m) => m.type === MeldType.KongConcealed);
    const openingCost =
      isNewMeld && candidate.type !== PlayerActionType.KongConcealed
        ? 2 -
          (isCall ? tuning.callBonus : 0) +
          (isMenqing && o.wallRemaining > tuning.menqingCallLateWall ? tuning.menqingCallCost : 0)
        : 0;
    const isolatedHonors = tuning.honorKeep
      ? kinds.filter((k, i) => k >= 27 && kinds.indexOf(k) === i && kinds.lastIndexOf(k) === i)
          .length
      : 0;
    let score = folding
      ? -readRisk * tuning.foldRiskWeight -
        shanten * 10 +
        ukeire * 0.1 -
        (isNewMeld ? tuning.foldRiskWeight : 0)
      : -shanten * tuning.shantenWeight +
        ukeire * tuning.ukeireWeight +
        value(kinds) * (profile.style === AIStyle.Attack ? 3 : 1.5) +
        isolatedHonors * tuning.honorKeep -
        defencePenalty -
        openingCost;
    const melds = meldsAfter(o, candidate, isNewMeld);
    if (!folding && shanten >= 1 && shanten <= tuning.shapeShanten)
      score += shapeBonusOf(kinds, melds, tuning);
    if (profile.strength === AIStrength.High && shanten === 0) {
      let weightedValue = 0;
      for (const k of useful)
        weightedValue += unknown[k]! * (o.base + o.taiUnit * taiOf(o, melds, kinds, k, false));
      score += Math.min(
        tuning.tenpaiValueCap,
        (weightedValue / Math.max(1, ukeire) / Math.max(1, o.base)) * tuning.tenpaiValueScale
      );
    }
    scores.push({ index, score, shanten, kinds, nextFixed, useful });
    evaluated++;
    scores.sort((a, b) => b.score - a.score || a.index - b.index);
    best = scores[0]!.index;
    progress?.(result());
  }
  // 先完整比較所有候選，再把剩餘工作量用於前三名，避免固定牌種順序偏差。
  if (profile.strength === AIStrength.High && !truncated && !folding)
    for (const entry of scores.slice(0, tuning.lookaheadCandidates)) {
      const { kinds, nextFixed, shanten, useful } = entry;
      // 高強度多看有效摸牌後的最佳捨牌及下一層有效張數。只從未知池推演。
      if (profile.strength === AIStrength.High && shanten > 0) {
        let gain = 0;
        let weight = 0;
        const rng = createSeedRng(seed);
        const sampled = useful
          .map((kind) => ({ kind, order: rng() }))
          .sort((a, b) => a.order - b.order)
          .slice(0, tuning.lookaheadDraws);
        for (const { kind: draw } of sampled) {
          if (expired()) {
            truncated = true;
            break;
          }
          const drawn = [...kinds, draw];
          let nextScore = -Infinity;
          for (const discard of new Set(drawn)) {
            if (expired()) {
              truncated = true;
              break;
            }
            const remaining = [...drawn];
            remaining.splice(remaining.indexOf(discard), 1);
            const distance = analysis.shanten(remaining, nextFixed);
            if (distance >= shanten) continue;
            let nextUkeire = 0;
            for (let k = 0; k < 34; k++) {
              if (expired()) {
                truncated = true;
                break;
              }
              const available = unknown[k]! - (k === draw ? 1 : 0);
              if (available > 0 && analysis.shanten([...remaining, k], nextFixed) < distance)
                nextUkeire += available;
            }
            nextScore = Math.max(
              nextScore,
              (shanten - distance) * 3 + nextUkeire * tuning.lookaheadWeight
            );
          }
          if (truncated) break;
          if (Number.isFinite(nextScore)) {
            gain += nextScore * unknown[draw]!;
            weight += unknown[draw]!;
          }
        }
        if (truncated) break;
        entry.score += weight ? gain / weight : 0;
      }
      scores.sort((a, b) => b.score - a.score || a.index - b.index);
      best = scores[0]!.index;
      progress?.(result());
    }
  if (tuning.mistakeRate > 0 && scores.length > 1) {
    const alternatives = scores
      .filter(
        (s) =>
          (tuning.mistakeAnyShanten || s.shanten === scores[0]!.shanten) &&
          scores[0]!.score - s.score <= tuning.mistakeWindow
      )
      .slice(0, 3);
    const rng = createSeedRng(seed);
    if (alternatives.length > 1 && rng() < tuning.mistakeRate)
      best = alternatives[1 + Math.floor(rng() * (alternatives.length - 1))]!.index;
  }
  return result();
};

/** 決策亂數只與自身可見局面及獨立 AI seed 有關，重建／暫停不會額外消耗亂數。 */
export const decisionSeed = (observation: IAIObservation, seed: number): number => {
  let hash = seed >>> 0;
  for (const char of JSON.stringify(observation))
    hash = Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0;
  return hash;
};
